import React, { useEffect, useRef, useState, useCallback } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useAuth } from '../context/AuthContext';
import { useAdminWs, useAdminWsConnected } from '../context/AdminWsContext';
import { RefreshCw } from 'lucide-react';

function makeIcon(online) {
  return L.divIcon({
    className: '',
    html: `<div class="${online ? 'map-marker-online' : 'map-marker-offline'}"></div>`,
    iconSize: [18, 18],
    iconAnchor: [9, 9],
    popupAnchor: [0, -14],
  });
}

export const MapView = () => {
  const { fetchAuthorized } = useAuth();
  const wsOk = useAdminWsConnected();
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const mapContainerRef = useRef(null);
  const leafletRef      = useRef(null);
  const markersRef      = useRef({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchAuthorized('/api/dashboard/devices');
      const d   = await res.json();
      if (d.success) setDevices(d.devices);
    } catch {}
    finally { setLoading(false); }
  }, [fetchAuthorized]);

  // Init Leaflet once when component mounts
  useEffect(() => {
    if (!mapContainerRef.current || leafletRef.current) return;

    leafletRef.current = L.map(mapContainerRef.current, {
      center: [41.299, 69.240],
      zoom: 12,
    });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors',
      maxZoom: 19,
    }).addTo(leafletRef.current);

    return () => {
      leafletRef.current?.remove();
      leafletRef.current = null;
      markersRef.current = {};
    };
  }, []);

  // Rebuild markers when devices array changes
  useEffect(() => {
    const map = leafletRef.current;
    if (!map) return;

    Object.values(markersRef.current).forEach(m => m.remove());
    markersRef.current = {};

    devices.forEach(device => {
      if (!device.lat || !device.lng) return;

      const marker = L.marker([device.lat, device.lng], {
        icon: makeIcon(device.is_online),
      }).addTo(map);

      marker.bindPopup(`
        <div style="font-family:'Inter',sans-serif;min-width:180px;">
          <div style="font-weight:700;font-size:13px;margin-bottom:6px;color:#e2e8f0;">
            ${device.machine_name || device.device_id}
          </div>
          <div style="display:flex;align-items:center;gap:6px;font-size:12px;">
            <span style="display:inline-block;width:8px;height:8px;border-radius:50%;
              background:${device.is_online ? '#4ade80' : '#f87171'};"></span>
            <span style="color:${device.is_online ? '#4ade80' : '#f87171'};">
              ${device.is_online ? 'Онлайн' : 'Оффлайн'}
            </span>
          </div>
          <div style="margin-top:8px;font-size:11px;color:#94a3b8;">${device.lat}, ${device.lng}</div>
        </div>
      `);

      markersRef.current[device.device_id] = marker;
    });
  }, [devices]);

  // WS: update marker icon on status change without full reload
  const handleWs = useCallback((msg) => {
    if (msg.event === 'device_online') {
      setDevices(prev => prev.map(d =>
        d.device_id === msg.data.device_id ? { ...d, is_online: true, last_seen: new Date().toISOString() } : d
      ));
      const marker = markersRef.current[msg.data.device_id];
      if (marker) marker.setIcon(makeIcon(true));
    }
    if (msg.event === 'device_offline') {
      setDevices(prev => prev.map(d =>
        d.device_id === msg.data.device_id ? { ...d, is_online: false } : d
      ));
      const marker = markersRef.current[msg.data.device_id];
      if (marker) marker.setIcon(makeIcon(false));
    }
  }, []);

  useAdminWs(handleWs);
  useEffect(() => { load(); }, [load]);

  const flyTo = (lat, lng) => {
    leafletRef.current?.flyTo([lat, lng], 16, { duration: 1.2 });
  };

  const withCoords = devices.filter(d => d.lat && d.lng);

  return (
    <div className="fade-in">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1>Карта</h1>
          <p>Расположение киосков и статус в реальном времени</p>
        </div>
        <div className="page-header-actions">
          <span className={`badge ${wsOk ? 'badge-green' : 'badge-yellow'}`}>
            <span className={`dot ${wsOk ? 'dot-online' : 'dot-warning'}`} style={{ width: '6px', height: '6px' }} />
            {wsOk ? 'Live' : 'Подключение...'}
          </span>
          <button className="btn-icon" onClick={load} disabled={loading} title="Обновить">
            <RefreshCw size={14} className={loading ? 'spin' : ''} />
          </button>
        </div>
      </div>

      {/* Map card */}
      <div className="card" style={{ overflow: 'hidden' }}>
        <div ref={mapContainerRef} id="leaflet-map" />

        {/* Legend */}
        <div className="map-legend">
          <div className="map-legend-item">
            <span className="dot dot-online" />
            <span>Онлайн</span>
          </div>
          <div className="map-legend-item">
            <span className="dot dot-offline" />
            <span>Оффлайн</span>
          </div>
          <span className="text-dim" style={{ marginLeft: 'auto', fontSize: '11px' }}>
            Нажмите на маркер для деталей · OpenStreetMap
          </span>
        </div>
      </div>

      {/* Kiosk list */}
      <div className="card" style={{ marginTop: '14px', overflow: 'hidden' }}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--glass-border)', fontWeight: '600', fontSize: '13px' }}>
          Точки установки
        </div>
        <table>
          <thead>
            <tr>
              <th>Киоск</th>
              <th>Статус</th>
              <th>Координаты</th>
            </tr>
          </thead>
          <tbody>
            {withCoords.length > 0 ? withCoords.map(d => (
              <tr
                key={d.device_id}
                style={{ cursor: 'pointer' }}
                onClick={() => flyTo(d.lat, d.lng)}
                title="Перейти на карте"
              >
                <td>
                  <div className="fw-600">{d.machine_name || 'Unnamed'}</div>
                  <div className="mono text-dim" style={{ fontSize: '11px' }}>{d.device_id?.slice(0, 12)}…</div>
                </td>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                    <span className={`dot ${d.is_online ? 'dot-online' : 'dot-offline'}`} />
                    <span style={{ fontSize: '13px', color: d.is_online ? 'var(--green)' : 'var(--text-2)' }}>
                      {d.is_online ? 'Онлайн' : 'Оффлайн'}
                    </span>
                  </div>
                </td>
                <td className="mono text-dim" style={{ fontSize: '11px' }}>{d.lat}, {d.lng}</td>
              </tr>
            )) : (
              <tr>
                <td colSpan={3} className="empty">
                  {loading
                    ? 'Загрузка...'
                    : 'Нет устройств с координатами. Добавьте поля lat/lng в БД для каждого киоска.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
